import { CHIMES } from './registry';

// Short deterministic synthesized tones; no media downloads or third-party service.
export class AudioPlayer {
  constructor({ Context = window.AudioContext || window.webkitAudioContext,
    notify = () => {} } = {}) {
    Object.assign(this, { Context, notify });
    this.nodes = new Set();
    this.activated = false;
    this.disposed = false;
    this.generation = 0;
  }
  async activate() {
    const generation = this.generation;
    try {
      if (this.disposed || !this.Context) throw new Error('Audio unavailable');
      if (!this.context) {
        this.context = new this.Context();
        this.context.onstatechange = () => {
          if (!this.disposed && this.activated && this.context.state !== 'running') {
            this.activated = false;
            this.stop();
            this.notify('blocked');
          }
        };
      }
      await this.context.resume();
      if (this.disposed || generation !== this.generation) return false;
      this.activated = this.context.state === 'running';
      this.notify(this.activated ? 'ready' : 'blocked');
      return this.activated;
    } catch (_) {
      this.activated = false;
      if (!this.disposed) this.notify('blocked');
      return false;
    }
  }
  async preview(chime, volume) {
    this.stop();
    if (await this.activate()) return this.play(chime, volume);
    return false;
  }
  play(chime, volume) {
    const tone = CHIMES.find((c) => c.id === chime);
    if (this.disposed || !tone || !Number.isFinite(volume) || volume < 0 || volume > 1 ||
        !this.activated || this.context?.state !== 'running') {
      if (!this.disposed) this.notify('blocked');
      return Promise.resolve(false);
    }
    this.stop();
    const { context } = this;
    const start = context.currentTime + 0.01;
    tone.notes.forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const at = start + index * 0.14;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(volume * 0.16, at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.12);
      oscillator.connect(gain);
      gain.connect(context.destination);
      this.nodes.add(oscillator);
      oscillator.onended = () => {
        this.nodes.delete(oscillator);
        oscillator.disconnect();
        gain.disconnect();
      };
      oscillator.start(at);
      oscillator.stop(at + 0.13);
    });
    return new Promise((resolve) => {
      this.finish = resolve;
      this.timer = setTimeout(() => {
        this.finish = null;
        resolve(true);
      }, tone.notes.length * 140 + 50);
    });
  }
  stop() {
    this.generation += 1;
    clearTimeout(this.timer);
    if (this.finish) this.finish(false);
    this.finish = null;
    this.nodes.forEach((node) => { try { node.stop(); } catch (_) { /* already ended */ } });
    this.nodes.clear();
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.activated = false;
    this.stop();
    if (this.context) this.context.close().catch(() => {});
  }
}
